import {
  logger as defaultLogger,
  type ILogger,
} from '@principle-theorem/developer-tools';
import {
  type IBrand,
  type IOrganisation,
  type IPatient,
  type IPatientRelationship,
  PatientRelationshipType,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  bufferedQuery$,
  type DocumentReference,
  Firestore,
  type IDataTable,
  isSameRef,
  undeletedFilter,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, sortBy, startCase, uniq } from 'lodash';
import * as moment from 'moment-timezone';
import { reduce } from 'rxjs/operators';
import { Brand } from '../models/brand';
import { DuplicateContactKey } from './duplicate-contact-key';

/** Kept deliberately small so the whole brand's patient list fits in memory. */
interface IPatientContactRecord {
  patientRef: DocumentReference<IPatient>;
  patientName: string;
  patientUrl: string;
  relationships: IPatientRelationship[];
  emailKey?: string;
  phoneKeys: string[];
}

export interface IDuplicateContactRecord {
  matchValue: string;
  patientName: string;
  patientId: string;
  patientUrl: string;
  potentialDuplicates: string;
  unknownMatches: number;
  identicalNameMatches: number;
}

export interface IDuplicateContactSummaryRecord {
  matchType: DuplicateMatchType;
  sharedValues: number;
  patients: number;
  patientsWithUnknownMatches: number;
  patientsWithIdenticalNameMatches: number;
}

/** Not `AnyDataTable[]`, which is `IDataTable<any>` and erases the row type at every caller. */
export type DuplicateContactDetailsTables = [
  summary: IDataTable<IDuplicateContactSummaryRecord>,
  emails: IDataTable<IDuplicateContactRecord>,
  phoneNumbers: IDataTable<IDuplicateContactRecord>,
];

export enum DuplicateMatchType {
  Email = 'Email',
  PhoneNumber = 'Phone Number',
}

const PATIENT_PAGE_SIZE = 500;

/** Written in both directions by `Patient.markAsDuplicate`, so either side proves the merge. */
const mergeRelationshipTypes = [
  PatientRelationshipType.PrimaryPatient,
  PatientRelationshipType.DuplicatePatient,
];

const whitespaceRun = /\s+/g;

/**
 * Lists patients who share an email address or phone number so a practice can
 * record the relationships between them. Migrated data rarely carries these
 * links, so the shared detail is usually the only evidence that two profiles
 * belong to the same household — or to the same person twice.
 */
export class DuplicatePatientContactDetails {
  constructor(private _logger: ILogger = defaultLogger) {}

  /**
   * Returns a summary followed by one sheet per match type. Email and phone are
   * kept apart because they are worked as separate passes.
   */
  async run(
    brandRef: DocumentReference<IBrand>,
    appUrl: string
  ): Promise<DuplicateContactDetailsTables> {
    const startedAt = moment();
    this._logger.info(
      `[DuplicatePatientContactDetails] Starting for brand ${brandRef.id}`
    );

    const brand = await Firestore.getDoc(brandRef);
    const organisation = await Firestore.getDoc(
      Brand.organisationRef(asReffable(brandRef))
    );

    const contactRecords = await this._pagePatients(
      brand,
      organisation,
      appUrl
    );

    const groupingStartedAt = moment();
    const emailRows = this._findEmailMatches(contactRecords);
    const phoneRows = this._findPhoneMatches(contactRecords);

    this._logger.info(
      `[DuplicatePatientContactDetails] Grouped ${contactRecords.length} ` +
        `patients into ${emailRows.length} email + ` +
        `${phoneRows.length} phone rows in ` +
        `${moment().diff(groupingStartedAt, 'ms')}ms`
    );
    this._logger.info(
      `[DuplicatePatientContactDetails] Completed for brand ${brandRef.id} ` +
        `in ${moment().diff(startedAt, 'ms')}ms`
    );

    return [
      this._toSummaryTable(emailRows, phoneRows),
      this._toContactTable('Duplicate Emails', 'Duplicate Email', emailRows),
      this._toContactTable(
        'Duplicate Phone Numbers',
        'Duplicate Phone Number',
        phoneRows
      ),
    ];
  }

  /** Excel truncates a sheet name at 31 characters, so these stay short and the brand is carried by the file name. */
  private _toSummaryTable(
    emailRows: IDuplicateContactRecord[],
    phoneRows: IDuplicateContactRecord[]
  ): IDataTable<IDuplicateContactSummaryRecord> {
    return {
      name: 'Summary',
      data: [
        this._toSummaryRow(DuplicateMatchType.Email, emailRows),
        this._toSummaryRow(DuplicateMatchType.PhoneNumber, phoneRows),
      ],
      columns: [
        { key: 'matchType', header: 'Match Type' },
        { key: 'sharedValues', header: 'Shared Values' },
        { key: 'patients', header: 'Patients' },
        {
          key: 'patientsWithUnknownMatches',
          header: 'Patients With Unknown Matches',
        },
        {
          key: 'patientsWithIdenticalNameMatches',
          header: 'Patients With Identical Name Matches',
        },
      ],
    };
  }

  private _toSummaryRow(
    matchType: DuplicateMatchType,
    rows: IDuplicateContactRecord[]
  ): IDuplicateContactSummaryRecord {
    return {
      matchType,
      sharedValues: uniq(rows.map((row) => row.matchValue)).length,
      patients: uniq(rows.map((row) => row.patientId)).length,
      patientsWithUnknownMatches: uniq(
        rows.filter((row) => row.unknownMatches > 0).map((row) => row.patientId)
      ).length,
      patientsWithIdenticalNameMatches: uniq(
        rows
          .filter((row) => row.identicalNameMatches > 0)
          .map((row) => row.patientId)
      ).length,
    };
  }

  private _toContactTable(
    name: string,
    valueHeader: string,
    rows: IDuplicateContactRecord[]
  ): IDataTable<IDuplicateContactRecord> {
    return {
      name,
      data: rows,
      columns: [
        { key: 'matchValue', header: valueHeader },
        { key: 'patientName', header: 'Patient' },
        { key: 'patientId', header: 'Patient ID' },
        { key: 'patientUrl', header: 'Patient Link' },
        { key: 'potentialDuplicates', header: 'Potential Duplicates' },
        { key: 'unknownMatches', header: 'Unknown Matches' },
        { key: 'identicalNameMatches', header: 'Identical Name Matches' },
      ],
    };
  }

  private async _pagePatients(
    brand: WithRef<IBrand>,
    organisation: WithRef<IOrganisation>,
    appUrl: string
  ): Promise<IPatientContactRecord[]> {
    const pagingStartedAt = moment();
    let pageCount = 0;

    const contactRecords = await bufferedQuery$(
      Brand.patientCol(brand),
      PATIENT_PAGE_SIZE,
      'ref'
    )
      .pipe(
        reduce<WithRef<IPatient>[], IPatientContactRecord[]>(
          (accumulatedRecords, patientPage) => {
            const pageRecords = patientPage
              .filter(undeletedFilter)
              .map((patient) =>
                this._toContactRecord(patient, brand, organisation, appUrl)
              );
            pageCount += 1;
            accumulatedRecords.push(...pageRecords);
            this._logger.debug(
              `[DuplicatePatientContactDetails] Paged patient page ` +
                `${pageCount} (${patientPage.length} fetched, ` +
                `${pageRecords.length} undeleted, ` +
                `${accumulatedRecords.length} total) ` +
                `after ${moment().diff(pagingStartedAt, 'ms')}ms`
            );
            return accumulatedRecords;
          },
          []
        )
      )
      .toPromise();

    const records = contactRecords ?? [];
    this._logger.info(
      `[DuplicatePatientContactDetails] Paged ${records.length} patients ` +
        `across ${pageCount} page(s) in ` +
        `${moment().diff(pagingStartedAt, 'ms')}ms`
    );
    return records;
  }

  private _toContactRecord(
    patient: WithRef<IPatient>,
    brand: WithRef<IBrand>,
    organisation: WithRef<IOrganisation>,
    appUrl: string
  ): IPatientContactRecord {
    const contactNumbers = patient.contactNumbers ?? [];

    return {
      patientRef: patient.ref,
      patientName: patient.name,
      patientUrl: this._toPatientUrl(brand, patient.ref, appUrl),
      relationships: patient.relationships,
      emailKey: DuplicateContactKey.forEmail(patient.email),
      phoneKeys: uniq(
        compact(
          contactNumbers.map((contactNumber) =>
            DuplicateContactKey.forPhoneNumber(
              contactNumber.number,
              organisation.region
            )
          )
        )
      ),
    };
  }

  private _findEmailMatches(
    records: IPatientContactRecord[]
  ): IDuplicateContactRecord[] {
    const emailGroups = this._groupByKey(records, (record) =>
      compact([record.emailKey])
    );
    return this._buildContactRows(emailGroups);
  }

  private _findPhoneMatches(
    records: IPatientContactRecord[]
  ): IDuplicateContactRecord[] {
    const phoneGroups = this._groupByKey(records, (record) => record.phoneKeys);
    return this._buildContactRows(phoneGroups);
  }

  private _groupByKey(
    records: IPatientContactRecord[],
    resolveKeys: (record: IPatientContactRecord) => string[]
  ): Map<string, IPatientContactRecord[]> {
    const groups = new Map<string, IPatientContactRecord[]>();

    for (const record of records) {
      for (const key of resolveKeys(record)) {
        const group = groups.get(key);
        if (group) {
          group.push(record);
          continue;
        }
        groups.set(key, [record]);
      }
    }

    return groups;
  }

  /**
   * Sorted by shared value so everyone on the same email or number sits
   * together, which lets a whole household be resolved in one pass.
   */
  private _buildContactRows(
    groups: Map<string, IPatientContactRecord[]>
  ): IDuplicateContactRecord[] {
    const rows: IDuplicateContactRecord[] = [];

    for (const [groupKey, patients] of groups) {
      if (patients.length < 2) {
        continue;
      }
      rows.push(...this._toGroupRows(groupKey, patients));
    }

    return sortBy(rows, ['matchValue', 'patientName']);
  }

  private _toGroupRows(
    matchValue: string,
    patients: IPatientContactRecord[]
  ): IDuplicateContactRecord[] {
    return compact(
      patients.map((patient) => {
        const others = patients.filter(
          (other) =>
            !isSameRef(other.patientRef, patient.patientRef) &&
            !this._isAlreadyMerged(patient, other)
        );
        return others.length
          ? this._toContactRow(matchValue, patient, others)
          : undefined;
      })
    );
  }

  private _toContactRow(
    matchValue: string,
    patient: IPatientContactRecord,
    others: IPatientContactRecord[]
  ): IDuplicateContactRecord {
    const sortedOthers = sortBy(others, (other) => other.patientName);

    return {
      matchValue,
      patientName: patient.patientName,
      patientId: patient.patientRef.id,
      patientUrl: patient.patientUrl,
      potentialDuplicates: this._describeMatches(patient, sortedOthers),
      unknownMatches: sortedOthers.filter(
        (other) => !this._findRelationship(patient, other)
      ).length,
      identicalNameMatches: sortedOthers.filter((other) =>
        this._hasSameName(patient, other)
      ).length,
    };
  }

  /** Reads the relationship off the row's own patient, so it is their view of each match. */
  private _describeMatches(
    patient: IPatientContactRecord,
    others: IPatientContactRecord[]
  ): string {
    return others
      .map(
        (other) =>
          `${other.patientName} (${this._resolveRelationship(patient, other)})`
      )
      .join(', ');
  }

  /**
   * A pair staff have already merged is finished work, so re-listing it would
   * send them back to a decision they have made.
   */
  private _isAlreadyMerged(
    patientA: IPatientContactRecord,
    patientB: IPatientContactRecord
  ): boolean {
    return (
      this._hasMergeRelationship(patientA, patientB) ||
      this._hasMergeRelationship(patientB, patientA)
    );
  }

  private _hasMergeRelationship(
    patient: IPatientContactRecord,
    other: IPatientContactRecord
  ): boolean {
    return patient.relationships.some(
      (relationship) =>
        isSameRef(relationship.patient.ref, other.patientRef) &&
        mergeRelationshipTypes.includes(relationship.type)
    );
  }

  private _hasSameName(
    patient: IPatientContactRecord,
    other: IPatientContactRecord
  ): boolean {
    return (
      this._normaliseName(patient.patientName) ===
      this._normaliseName(other.patientName)
    );
  }

  private _normaliseName(name: string): string {
    return name.trim().toLowerCase().replace(whitespaceRun, ' ');
  }

  private _findRelationship(
    patient: IPatientContactRecord,
    other: IPatientContactRecord
  ): IPatientRelationship | undefined {
    return patient.relationships.find((candidate) =>
      isSameRef(candidate.patient.ref, other.patientRef)
    );
  }

  private _resolveRelationship(
    patient: IPatientContactRecord,
    other: IPatientContactRecord
  ): string {
    const relationship = this._findRelationship(patient, other);
    return startCase(relationship?.type ?? PatientRelationshipType.Unknown);
  }

  private _toPatientUrl(
    brand: WithRef<IBrand>,
    patientRef: DocumentReference<IPatient>,
    appUrl: string
  ): string {
    return [appUrl, brand.slug, 'patients', patientRef.id].join('/');
  }
}
